import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AdminAPI } from '../services/portalApi';

type LoginStage = 'unauthenticated' | 'passkey_verified' | 'fully_authenticated';

interface PortalAdmin {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
}

interface PortalAuthContextType {
  admin: PortalAdmin | null;
  token: string | null;
  stage: LoginStage;
  loading: boolean;
  setPasskeyToken: (token: string) => void;
  setFullToken: (token: string) => void;
  logout: () => void;
}

const PortalAuthContext = createContext<PortalAuthContextType | null>(null);

export const usePortalAuth = () => {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortalAuth must be used within PortalAuthProvider');
  return ctx;
};

export const PortalAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<PortalAdmin | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [stage, setStage] = useState<LoginStage>('unauthenticated');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = sessionStorage.getItem('ip_portal_token');
    if (stored) {
      setToken(stored);
      // Try to fetch admin info
      AdminAPI.getMe()
        .then((res) => {
          setAdmin(res.data);
          setStage('fully_authenticated');
        })
        .catch(() => {
          sessionStorage.removeItem('ip_portal_token');
          setToken(null);
          setStage('unauthenticated');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const setPasskeyToken = (t: string) => {
    sessionStorage.setItem('ip_portal_token', t);
    setToken(t);
    setStage('passkey_verified');
  };

  const setFullToken = (t: string) => {
    sessionStorage.setItem('ip_portal_token', t);
    setToken(t);
    setStage('fully_authenticated');
    // Fetch admin info
    AdminAPI.getMe().then((res) => setAdmin(res.data)).catch(() => {});
  };

  const logout = () => {
    sessionStorage.removeItem('ip_portal_token');
    setToken(null);
    setAdmin(null);
    setStage('unauthenticated');
  };

  return (
    <PortalAuthContext.Provider value={{ admin, token, stage, loading, setPasskeyToken, setFullToken, logout }}>
      {children}
    </PortalAuthContext.Provider>
  );
};
